import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../app/store"; // Adjust import based on location
import logo from "../assets/logo.png";
import ThemeSwitch from "../component/ThemeSwitch";
import {
  changePassword,
  resetForgotPasswordState,
} from "../features/auth/forgotPasswordSlice";

const Forgot: React.FC = () => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false); // State for password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false); // State for confirm password visibility
  const dispatch = useDispatch<AppDispatch>(); // Type dispatch as AppDispatch
  // const token = queryParams.get("token");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { token } = useParams();

  const { loading } = useSelector((state: RootState) => state.forgotPassword); // Extract loading, error, and successMessage from Redux store
  // Constant time string comparison function
  function constantTimeCompare(str1: string, str2: string): boolean {
    if (str1.length !== str2.length) return false;
    let result = 0;
    for (let i = 0; i < str1.length; i++) {
      result |= str1.charCodeAt(i) ^ str2.charCodeAt(i);
    }
    return result === 0;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(resetForgotPasswordState());

    // Validate password and confirmPassword match
    if (!constantTimeCompare(password, confirmPassword)) {
      setError("Passwords do not match!");
      return;
    }
    if (token) {
      dispatch(changePassword({ token: token, newPassword: password }))
        .unwrap()
        .then((response) => {
          setMessage(response.message);
          navigate("/login");
          setPassword("");
          setConfirmPassword("");
        })
        .catch((error) => {
          setError(error.message);
        });
    }
    // dispatch(setError(error));
  };
  return (
    <div className="h-screen w-screen bg-[#F0F0F0] overflow-hidden dark:bg-gradient-to-r dark:from-[#07131C] dark:to-[#0C2538] md:pt-10 pt-0">
      <div className="auth_container">
        <div className="flex justify-end lg:-mr-10">
          <ThemeSwitch />
        </div>
        <div className="max-w-[45.5rem] w-full mt-10">
          <img alt="Your Company" src={logo} className="w-auto h-20" />
          <h2 className="mt-10 auth_head font-bold dark:text-white font-ProximaLight !duration-0">
            <span className="text-[#265376]"> Change </span> <br /> Password
          </h2>
          <div className="mt-10 max-w-[27.25rem]">
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              autoComplete="off"
            >
              <div className="relative dark:bg-[#031523] bg-[#e4e4e4] rounded-xl backdrop-blur-[20px]">
                <input
                  aria-autocomplete="none"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"} // Toggle between text and password
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder=" "
                  autoComplete="new-password"
                  className="auth_input block font-ProximaLight px-2.5 py-4 w-full text-sm text-black bg-transparent rounded-lg focus:outline-none appearance-none dark:text-white peer"
                />
                <label
                  htmlFor="password"
                  className="absolute text-sm text-[#5E829F] dark:text-[#5E829F] duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 peer-focus:px-2  peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
                >
                  Password
                </label>
                <button
                  className="absolute text-black transform -translate-y-1/2 dark:text-white top-1/2 right-3"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M12 9a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5a5 5 0 0 1 5-5a5 5 0 0 1 5 5a5 5 0 0 1-5 5m0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 256 256"
                    >
                      <path
                        fill="currentColor"
                        d="M228 175a8 8 0 0 1-10.92-3l-19-33.2A123.2 123.2 0 0 1 162 155.46l5.87 35.22a8 8 0 0 1-6.58 9.21a8.4 8.4 0 0 1-1.29.11a8 8 0 0 1-7.88-6.69l-5.77-34.58a133 133 0 0 1-36.68 0l-5.77 34.58A8 8 0 0 1 96 200a8.4 8.4 0 0 1-1.32-.11a8 8 0 0 1-6.58-9.21l5.9-35.22a123.2 123.2 0 0 1-36.06-16.69L39 172a8 8 0 1 1-13.94-8l20-35a153.5 153.5 0 0 1-19.3-20a8 8 0 1 1 12.46-10c16.6 20.54 45.64 45 89.78 45s73.18-24.49 89.78-45a8 8 0 1 1 12.44 10a153.5 153.5 0 0 1-19.3 20l20 35a8 8 0 0 1-2.92 11"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Password Input */}

              {/* Confirm Password Input */}
              <div className="relative dark:bg-[#031523] bg-[#e4e4e4] rounded-xl backdrop-blur-[20px]">
                <input
                  aria-autocomplete="none"
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"} // Toggle between text and password
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="auth_input block font-ProximaLight px-2.5 py-4 w-full text-sm text-black bg-transparent rounded-lg focus:outline-none appearance-none dark:text-white peer"
                  placeholder=" "
                />
                <label
                  htmlFor="confirmPassword"
                  className="absolute text-sm text-[#5E829F] dark:text-[#5E829F] duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 peer-focus:px-2  peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
                >
                  Confirm Password
                </label>
                <button
                  className="absolute text-black transform -translate-y-1/2 dark:text-white top-1/2 right-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  type="button"
                >
                  {showConfirmPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M12 9a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5a5 5 0 0 1 5-5a5 5 0 0 1 5 5a5 5 0 0 1-5 5m0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 256 256"
                    >
                      <path
                        fill="currentColor"
                        d="M228 175a8 8 0 0 1-10.92-3l-19-33.2A123.2 123.2 0 0 1 162 155.46l5.87 35.22a8 8 0 0 1-6.58 9.21a8.4 8.4 0 0 1-1.29.11a8 8 0 0 1-7.88-6.69l-5.77-34.58a133 133 0 0 1-36.68 0l-5.77 34.58A8 8 0 0 1 96 200a8.4 8.4 0 0 1-1.32-.11a8 8 0 0 1-6.58-9.21l5.9-35.22a123.2 123.2 0 0 1-36.06-16.69L39 172a8 8 0 1 1-13.94-8l20-35a153.5 153.5 0 0 1-19.3-20a8 8 0 1 1 12.46-10c16.6 20.54 45.64 45 89.78 45s73.18-24.49 89.78-45a8 8 0 1 1 12.44 10a153.5 153.5 0 0 1-19.3 20l20 35a8 8 0 0 1-2.92 11"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  className="mt-5 flex w-full justify-center rounded-2xl bg-[#265376] px-3 py-4 text-white hover:bg-[#1F374A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9ca3af87]"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Submit"}
                </button>
              </div>

              {/* Display Error or Success Message */}
              {error && <p className="text-red-500">{error}</p>}
              {message && <p className="text-white">{message}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forgot;
